import axiosInstance from './axios-instance';
import { invalidateOrderDataCache, resolveClientId } from './cache-invalidation';
import { isMutationSuccess, toPositiveNumber } from './cache-utils';

export type GetPOsQueryParams = {
  clientId?: number;
  supplierId?: number;
  accountId?: number;
};

// --- HELPERS ---

export const getScopedAccountId = (accountId?: number): number => {
  if (typeof window === 'undefined') {
    return Number(accountId || 0);
  }
  const ownerAccountId = Number(localStorage.getItem('ownerAccountId') || '0');
  if (ownerAccountId > 0) {
    return ownerAccountId;
  }
  if (Number(accountId) > 0) {
    return Number(accountId);
  }
  return Number(localStorage.getItem('accountId') || '0');
};

const collectPurchaseOrderOrderIds = (...sources: any[]): number[] => {
  const orderIds = new Set<number>();

  const add = (value: any) => {
    const orderId = toPositiveNumber(value);
    if (orderId) {
      orderIds.add(orderId);
    }
  };

  const addComponent = (component: any) => {
    add(component?.order?.id);
    add(component?.orderId);
    add(component?.draperyOrderId);
    add(component?.itemId);
  };

  sources.forEach((source) => {
    const data = source?.data || source;

    (data?.orderIds || []).forEach(add);

    const purchaseOrderBody = data?.purchaseOrderBody;
    if (Array.isArray(purchaseOrderBody)) {
      purchaseOrderBody.forEach((room) => {
        (room?.components || []).forEach(addComponent);
      });
    }
  });

  return Array.from(orderIds);
};

const invalidatePurchaseOrderCaches = (payload: any, responseData: any) => {
  const clientId = resolveClientId(payload, responseData);
  const orderIds = collectPurchaseOrderOrderIds(payload, responseData);

  if (!orderIds.length) {
    invalidateOrderDataCache(undefined, clientId);
    return;
  }

  orderIds.forEach((orderId) => invalidateOrderDataCache(orderId, clientId));
};

// --- ROUTES ---

export const getPOComponents = async (queryParams: GetPOsQueryParams) => {
  try {
    const scopedAccountId = getScopedAccountId(queryParams?.accountId);

    const response = await axiosInstance.get(`purchaseOrder/get-components-po`, {
      params: {
        ...queryParams,
        accountId: scopedAccountId,
      },
    });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const createPO = async (payload: any) => {
  try {
    const scopedAccountId = getScopedAccountId(payload?.accountId);

    let managed_payload = {
      ...payload,
      accountId: scopedAccountId,
    };
    const response = await axiosInstance.post(`purchaseOrder/create`, managed_payload);
    if (isMutationSuccess(response.data)) {
      invalidatePurchaseOrderCaches(managed_payload, response.data);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const editPOs = async (payload: any) => {
  try {
    const scopedAccountId = getScopedAccountId(payload?.accountId);

    const managedPayload = {
      ...payload,
      accountId: scopedAccountId,
    };
    const response = await axiosInstance.post(`purchaseOrder/edit`, managedPayload);
    if (isMutationSuccess(response.data)) {
      invalidatePurchaseOrderCaches(managedPayload, response.data);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getPOs = async (queryParams: GetPOsQueryParams) => {
  try {
    const scopedAccountId = getScopedAccountId(queryParams?.accountId);

    const response = await axiosInstance.get(`purchaseOrder/get-all`, {
      params: {
        ...queryParams,
        accountId: scopedAccountId,
      },
    });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getPO = async (queryParams: GetPOsQueryParams & { id: number }) => {
  try {
    const scopedAccountId = getScopedAccountId(queryParams?.accountId);

    const response = await axiosInstance.get(`purchaseOrder/get`, {
      params: {
        ...queryParams,
        accountId: scopedAccountId,
      },
    });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const sendPO = async (payload: any) => {
  try {
    const scopedAccountId = getScopedAccountId(payload?.accountId);

    const managedPayload = {
      ...payload,
      accountId: scopedAccountId,
    };
    const response = await axiosInstance.post(`purchaseOrder/send`, managedPayload);
    if (isMutationSuccess(response.data)) {
      invalidatePurchaseOrderCaches(managedPayload, response.data);
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const downloadPOPdf = async (payload: any) => {
  try {
    const scopedAccountId = getScopedAccountId(payload?.accountId);

    const managedPayload = {
      ...payload,
      accountId: scopedAccountId,
    };
    const response = await axiosInstance.post(`purchaseOrder/download-pdf`, managedPayload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updatePO = async (payload: any) => {
  try {
    return editPOs(payload);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getPOsComponents = async (queryParams: GetPOsQueryParams) => {
  try {
    return getPOComponents(queryParams);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const createPOs = async (payload: any) => {
  try {
    return createPO(payload);
  } catch (error) {
    console.error(error);
    throw error;
  }
};
